import type { Loan } from "./types";
import type { ColCtx, ColSpec } from "../grid/columns";
import { withExportReceipt } from "./exportReceipt";
// The caller supplies exactly the visible rows and columns. Strings remain text cells,
// including leading '='; ExcelJS never turns user-entered text into a formula.
export async function exportWorkbook(loans: Loan[], columns: ColSpec[], ctx: ColCtx) {
  const { default: ExcelJS } = await import("exceljs");
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet("Pipeline", { views: [{ state: "frozen", xSplit: 1, ySplit: 1 }] });
  const visible = columns.filter((c) => c.id !== "comp" || loans.some((l) => l.comp !== null));
  sheet.columns = visible.map((c) => ({ header: c.title, key: c.id, width: Math.max(12, Math.min(50, c.width / 7)) }));
  sheet.getRow(1).font = { bold: true, color: { argb: "FFFFFFFF" } };
  sheet.getRow(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF16396B" } };
  for (const loan of loans) {
    sheet.addRow(visible.map((c) => {
      if (c.id === "comp") return loan.comp?.commission === null || !loan.comp ? null : Number(loan.comp.commission);
      if (["amount", "price", "rev_pct", "revenue_amount"].includes(c.id)) {
        const v = loan[c.id as keyof Loan];
        return v === null ? null : Number(v);
      }
      return c.kind === "date" && c.field ? loan[c.field] : c.raw(loan, ctx);
    }));
  }
  if (visible.length) sheet.autoFilter = { from: { row: 1, column: 1 }, to: { row: Math.max(1, loans.length + 1), column: visible.length } };
  return workbook;
}
// The server records the export (who, which loan ids) first; without that receipt no file is built.
export async function downloadView(loans: Loan[], columns: ColSpec[], ctx: ColCtx) {
  const workbook = await withExportReceipt({ resource_type: "loan", item_ids: loans.map((l) => l.id), format: "xlsx" }, () =>
    exportWorkbook(loans, columns, ctx),
  );
  const bytes = await workbook.xlsx.writeBuffer();
  const blob = new Blob([new Uint8Array(bytes)], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = "pipeline-view.xlsx";
  a.click();
  window.setTimeout(() => URL.revokeObjectURL(url), 1000);
}
